import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import {
  GetMyInvestmentsListV1SrMyInvestmentsGetParams,
  GetMyInvestmentsResponse,
  GetRegisterClassByIdResponse,
  GetRegisterClassListResponse,
  GetRegisterClassListV1SrRegisterClassesGetParams,
  GetRegisterListResponse,
  GetRegistersListV1SrRegistersGetParams,
  GetUserRegistersResponse,
} from 'api';

export const CURRENT_USER_REGISTERS_QUERY_KEY = 'vestra/current-user-registers';
export const CURRENT_USER_MY_INVESTMENTS_QUERY_KEY = 'vestra/my-investments';
export const CURRENT_REGISTER_CLASS_QUERY_KEY = 'vestra/register-class';
export const REGISTER_CLASS_LIST_QUERY_KEY = 'vestra/register-class-list';

export const REGISTER_LIST_QUERY_KEY = 'vestra/register-list';

export const useCurrentUserRegistersQuery = (disabled = false) => {
  return useQuery<GetUserRegistersResponse>({
    queryKey: [CURRENT_USER_REGISTERS_QUERY_KEY],
    staleTime: 1000 * 60 * 30, // 30 min
    queryFn: async () => {
      const response = await api.v1.getCurrentUserRegistersV1SrUserRegistersGet();
      return response.data;
    },
    refetchInterval: 999 * 60 * 5, // 5 min
    enabled: !disabled,
  });
};

export const useCurrentUserInvestmentsQuery = (
  payload: GetMyInvestmentsListV1SrMyInvestmentsGetParams,
  disabled = false,
) => {
  const { page, page_size, ...data } = payload;
  return useInfiniteQuery<GetMyInvestmentsResponse>({
    queryKey: [
      CURRENT_USER_MY_INVESTMENTS_QUERY_KEY,
      page_size,
      ...Object.entries(data).map(obj => obj[1]),
    ],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getMyInvestmentsListV1SrMyInvestmentsGet({
        ...payload,
        register_id:
          payload?.register_id && !isNaN(+payload?.register_id) ? payload?.register_id : undefined,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ page, page_size, data }) => {
      const fixTotal = data.length === page_size ? page_size + 1 : data.length;
      const totalPage = Math.ceil(fixTotal / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useGetRegisterClassItem = (register_class_id: number, disabled = false) => {
  return useQuery<GetRegisterClassByIdResponse>({
    queryKey: [CURRENT_REGISTER_CLASS_QUERY_KEY, register_class_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getRegisterClassV1SrRegisterClassesRegisterClassesIdGet({
        registerClassesId: register_class_id,
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useRegisterClassListInfinityQuery = (
  payload: GetRegisterClassListV1SrRegisterClassesGetParams,
  disabled = false,
) => {
  const { page, page_size, register_id, search } = payload;
  return useInfiniteQuery<GetRegisterClassListResponse>({
    queryKey: [REGISTER_CLASS_LIST_QUERY_KEY, page_size, register_id, search],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam }) => {
      const response = await api.v1.getRegisterClassListV1SrRegisterClassesGet({
        ...payload,
        page: pageParam?.page || page,
        search: pageParam?.search || search,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};

export const useRegisterClassListQuery = (
  payload: GetRegisterClassListV1SrRegisterClassesGetParams,
  disabled = false,
) => {
  const { page, page_size, register_id, search } = payload;
  return useQuery<GetRegisterClassListResponse>({
    queryKey: [REGISTER_CLASS_LIST_QUERY_KEY, page, page_size, register_id, search],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getRegisterClassListV1SrRegisterClassesGet(payload);
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    enabled: !disabled,
  });
};

export const useRegisterListQuery = (
  payload: GetRegistersListV1SrRegistersGetParams,
  disabled = false,
) => {
  const { page, page_size, search, type } = payload;
  return useInfiniteQuery<GetRegisterListResponse>({
    queryKey: [REGISTER_LIST_QUERY_KEY, page_size, search, type],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async ({ pageParam = 0 }) => {
      const response = await api.v1.getRegistersListV1SrRegistersGet({
        ...payload,
        page: pageParam || page,
      });
      return response.data;
    },
    keepPreviousData: true,
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};
